import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { User } from '../../types';
import { casinoService, CasinoInfo, RouletteGame, RouletteBet } from '../../services/casinoService';
import { useError } from '../../contexts/ErrorContext';

interface RouletteProps {
  user: User;
  onUpdate: () => void;
}

const Roulette: React.FC<RouletteProps> = ({ user, onUpdate }) => {
  const [casinos, setCasinos] = useState<CasinoInfo[]>([]);
  const [selectedCasino, setSelectedCasino] = useState<CasinoInfo | null>(null);
  const [currentGame, setCurrentGame] = useState<RouletteGame | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCasinos, setLoadingCasinos] = useState(true);
  const [selectedChip, setSelectedChip] = useState<number>(10);
  const [currentBets, setCurrentBets] = useState<RouletteBet[]>([]);
  const [spinning, setSpinning] = useState(false);
  const [spinPosition, setSpinPosition] = useState(0);
  const [lastWinningNumber, setLastWinningNumber] = useState<number | null>(null);
  const [rouletteHistory, setRouletteHistory] = useState<number[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const { showError, showSuccess } = useError();

  const chipValues = [1, 5, 10, 25, 50, 100, 500, 1000];

  // Roulette wheel numbers in order for horizontal display
  // Add numbers before 0 to allow spinning in both directions
  const wheelNumbers = useMemo(() => [26, 3, 35, 12, 28, 7, 29, 18, 22, 9, 31, 14, 20, 1, 33, 16, 24, 5, 10, 23, 8, 30, 11, 36, 13, 27, 6, 34, 17, 25, 2, 21, 4, 19, 15, 32, 0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26], []);

  const loadCasinos = useCallback(async () => {
    setLoadingCasinos(true);
    try {
      const result = await casinoService.getCasinosByLocation(user.location);
      if (result.success) {
        const rouletteCasinos = result.casinos.filter((casino: CasinoInfo) => casino.type === 'roulette');
        setCasinos(rouletteCasinos);
        if (rouletteCasinos.length > 0) {
          setSelectedCasino(rouletteCasinos[0]);
        }
      }
    } catch (error) {
      console.error('Error loading casinos:', error);
      showError('Failed to load casinos. Please try again.');
    } finally {
      setLoadingCasinos(false);
    }
  }, [user.location, showError]);

  const loadRouletteHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const result = await casinoService.getRouletteHistory();
      if (result.success) {
        setRouletteHistory(result.history);
        // Set the last winning number from history
        if (result.history.length > 0) {
          setLastWinningNumber(result.history[result.history.length - 1]);
        }
      }
    } catch (error) {
      console.error('Error loading roulette history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadCasinos();
    loadRouletteHistory();
  }, [loadCasinos, loadRouletteHistory]);

  const startGame = useCallback(async () => {
    if (!selectedCasino) {
      showError('Please select a casino');
      return;
    }

    setLoading(true);
    try {
      const result = await casinoService.startRouletteGame(selectedCasino.id);
      if (result.success) {
        setCurrentGame(result.game);
        setCurrentBets(result.game.bets || []);
        
        // Set initial wheel position based on last winning number or 0
        if (lastWinningNumber !== null) {
          const lastIndex = wheelNumbers.indexOf(lastWinningNumber);
          if (lastIndex !== -1) {
            const numberWidth = 60;
            const centerOffset = 300;
            const arrowOffset = 15;
            const initialPosition = -(lastIndex * numberWidth) + centerOffset + arrowOffset;
            setSpinPosition(initialPosition);
          }
        } else {
          // Start at 0 if no previous game
          const zeroIndex = wheelNumbers.indexOf(0);
          if (zeroIndex !== -1) {
            const numberWidth = 60;
            const centerOffset = 300;
            const arrowOffset = 15;
            const initialPosition = -(zeroIndex * numberWidth) + centerOffset + arrowOffset;
            setSpinPosition(initialPosition);
          }
        }
        
        if (result.game.bets && result.game.bets.length > 0) {
          showSuccess('Resumed existing roulette game!');
        } else {
          showSuccess('Roulette game started!');
        }
      } else {
        showError(result.message || 'Failed to start game');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error starting game';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedCasino, lastWinningNumber, showError, showSuccess, wheelNumbers]);

  // Auto-start/resume game when casinos and history are loaded
  useEffect(() => {
    if (selectedCasino && !loadingCasinos && !loadingHistory && !currentGame) {
      startGame();
    }
  }, [selectedCasino, loadingCasinos, loadingHistory, currentGame, startGame]);

  const placeBet = async (type: RouletteBet['type'], value: number | string) => {
    if (!currentGame || !selectedCasino) return;

    const payouts = {
      'number': 35,
      'color': 1,
      'odd': 1,
      'even': 1,
      'low': 1,
      'high': 1,
      'dozen': 2,
      'column': 2
    };

    const bet: RouletteBet = {
      type,
      value,
      amount: selectedChip,
      payout: payouts[type]
    };

    setLoading(true);
    try {
      const result = await casinoService.placeRouletteBet(currentGame.gameId, selectedCasino.id, bet);
      if (result.success) {
        setCurrentGame(result.game);
        setCurrentBets(result.game.bets);
        onUpdate(); // Refresh user money
        showSuccess('Bet placed!');
      } else {
        showError(result.message || 'Failed to place bet');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error placing bet';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const spinWheel = async () => {
    if (!currentGame || !selectedCasino) return;

    setSpinning(true);
    setLoading(true);

    try {
      const result = await casinoService.spinRoulette(currentGame.gameId, selectedCasino.id);
      if (result.success) {
        // Find the position of the winning number in the wheel
        const winningNumber = result.game.winningNumber;
        const winningIndex = wheelNumbers.indexOf(winningNumber);
        
        if (winningIndex === -1) {
          console.error('Winning number not found in wheel:', winningNumber);
          setSpinning(false);
          setLoading(false);
          return;
        }

        // Calculate the final position to center the winning number under the pointer
        const numberWidth = 60; // 60px per number
        const centerOffset = 300; // Center of the visible area (assuming 600px width, center is 300px)
        const arrowOffset = 15; // Half the arrow width to center it over the number
        const finalPosition = -(winningIndex * numberWidth) + centerOffset + arrowOffset;

        // Animate the horizontal spin
        const spinDuration = 4000; // 4 seconds total
        const spinSteps = 120;
        const stepDuration = spinDuration / spinSteps;
        
        // Calculate multiple rotations (5 full rotations)
        const wheelWidth = wheelNumbers.length * 60; // Total width of one complete wheel
        const rotations = 5;
        const totalSpinDistance = wheelWidth * rotations;
        
        // Start from a random position
        const startPosition = Math.random() * -500 - 200;
        
        for (let i = 0; i < spinSteps; i++) {
          setTimeout(() => {
            const progress = i / spinSteps;
            
            // First 60% of animation: fast spinning
            if (progress < 0.6) {
              const fastProgress = progress / 0.6;
              const fastDistance = totalSpinDistance * fastProgress;
              setSpinPosition(startPosition - fastDistance);
            } 
            // Middle 25% of animation: start slowing down
            else if (progress < 0.85) {
              const slowStartProgress = (progress - 0.6) / 0.25;
              const slowEase = 1 - Math.pow(1 - slowStartProgress, 3); // Cubic ease-out
              
              const fastDistance = totalSpinDistance * 0.6;
              const remainingDistance = finalPosition - (startPosition - fastDistance);
              const currentSlowDistance = remainingDistance * slowEase;
              
              setSpinPosition(startPosition - fastDistance + currentSlowDistance);
            }
            // Last 15% of animation: very slow final approach
            else {
              const finalProgress = (progress - 0.85) / 0.15;
              const finalEase = 1 - Math.pow(1 - finalProgress, 4); // Quartic ease-out
              
              const fastDistance = totalSpinDistance * 0.6;
              const slowDistance = (finalPosition - (startPosition - fastDistance)) * 0.8; // 80% of remaining distance
              const finalDistance = (finalPosition - (startPosition - fastDistance)) * 0.2; // Last 20% of remaining distance
              
              setSpinPosition(startPosition - fastDistance + slowDistance + (finalDistance * finalEase));
            }
          }, i * stepDuration);
        }

        setTimeout(() => {
          // Set final position to exactly center the winning number
          setSpinPosition(finalPosition);
          setCurrentGame(result.game);
          setLastWinningNumber(result.game.winningNumber);
          setSpinning(false);
          setLoading(false);
          onUpdate(); // Refresh user money
          
          // Refresh roulette history to show the new number
          loadRouletteHistory();
          
          // Reset game state to allow multiple spins
          const updatedGame = { ...result.game, gameState: 'betting' as const };
          setCurrentGame(updatedGame);
          
          const totalWinnings = result.game.results.reduce((sum: number, r: { bet: RouletteBet; won: boolean; payout: number }) => sum + r.payout, 0);
          if (totalWinnings > 0) {
            showSuccess(`You won $${totalWinnings.toLocaleString()}!`);
          } else {
            showError('Better luck next time!');
          }
        }, spinDuration);
      } else {
        showError(result.message || 'Failed to spin');
        setSpinning(false);
        setLoading(false);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error spinning';
      showError(errorMessage);
      setSpinning(false);
      setLoading(false);
    }
  };

  const clearBets = () => {
    setCurrentBets([]);
  };

  const getNumberColor = (number: number): string => {
    if (number === 0) return 'green';
    const redNumbers = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];
    return redNumbers.includes(number) ? 'red' : 'black';
  };

  const getBetAmount = (type: string, value: number | string): number => {
    return currentBets
      .filter(bet => bet.type === type && bet.value === value)
      .reduce((sum, bet) => sum + bet.amount, 0);
  };

  return (
    <div className="roulette-page">
      <h2 className="page-title">Roulette</h2>
      
      <div className="roulette-content">
        {/* Casino Selection */}
        <div className="casino-selection">
          <h3>Available Roulette Tables</h3>
          {loadingCasinos ? (
            <div className="loading-casinos">
              <p>Loading casinos in {user.location}...</p>
            </div>
          ) : casinos.length > 0 ? (
            <div className="casino-list">
              {casinos.map((casino) => (
                <div 
                  key={casino.id}
                  className={`casino-card ${selectedCasino?.id === casino.id ? 'selected' : ''}`}
                  onClick={() => setSelectedCasino(casino)}
                >
                  <h4>{casino.name}</h4>
                  <p>Owner: {casino.ownerName || 'None'}</p>
                  <p>Max Bet: ${casino.maxBet.toLocaleString()}</p>
                  <p className={`house-balance ${casino.currentBalance < 0 ? 'negative' : casino.currentBalance > 0 ? 'positive' : 'neutral'}`}>
                    House Balance: ${casino.currentBalance.toLocaleString()}
                    {casino.currentBalance < 0 && <span className="balance-warning">⚠️ In Debt</span>}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-casinos">
              <p>No Roulette tables available in {user.location}.</p>
              <p>Try traveling to a different location!</p>
            </div>
          )}
        </div>

        {/* Game Interface */}
        {!currentGame ? (
          /* Start Game Section */
          selectedCasino && (
            <div className="start-game-section">
              <h3>Start Roulette Game</h3>
              <button
                className="start-button"
                onClick={startGame}
                disabled={loading}
              >
                {loading ? 'Starting...' : 'Start Game'}
              </button>
            </div>
          )
        ) : (
          /* Game Play Section */
          <div className="game-play-section">
            <h3>Roulette Game</h3>
            


            {/* Roulette Wheel */}
            <div className="roulette-wheel-section">
              <h4>Roulette Wheel</h4>
              <div className="wheel-container">
                <div className="wheel-pointer">▼</div>
                <div className="wheel-track">
                  <div 
                    className="wheel-numbers"
                    style={{ transform: `translateX(${spinPosition}px)` }}
                  >
                    {/* Repeat the numbers 10 times to ensure continuous scrolling during fast spins */}
                    {Array.from({ length: 10 }, () => wheelNumbers).flat().map((number, index) => (
                      <div
                        key={index}
                        className={`wheel-number ${getNumberColor(number)}`}
                      >
                        {number}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {currentGame.gameState === 'complete' && currentGame.winningNumber !== null && (
                <div className="winning-number">
                  <h4>Winning Number: {currentGame.winningNumber}</h4>
                  <div className={`winning-color ${currentGame.winningColor}`}>
                    {currentGame.winningColor}
                  </div>
                </div>
              )}

              {/* Recent Winners - Simplified and below wheel */}
              <div className="recent-winners-section">
                <h5>Recent Winners</h5>
                {loadingHistory ? (
                  <div className="loading-history">
                    <p>Loading...</p>
                  </div>
                ) : rouletteHistory.length > 0 ? (
                  <div className="recent-numbers">
                    {rouletteHistory.slice(-10).reverse().map((number, index) => (
                      <div
                        key={index}
                        className={`recent-number ${getNumberColor(number)}`}
                      >
                        {number}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-history">
                    <p>No previous numbers yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Betting Board */}
            <div className="betting-board">
              <h4>Place Your Bets</h4>
              
              {/* Numbers Grid */}
              <div className="numbers-grid">
                <button
                  className="zero"
                  onClick={() => placeBet('number', 0)}
                  disabled={loading || spinning}
                >
                  0
                  {getBetAmount('number', 0) > 0 && (
                    <div className="bet-chip">${getBetAmount('number', 0)}</div>
                  )}
                </button>
                <div className="numbers-container">
                  {/* Row 1: 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36 */}
                  <div className="number-row">
                    {[3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36].map((number) => (
                      <button
                        key={number}
                        className={`number ${getNumberColor(number)}`}
                        onClick={() => placeBet('number', number)}
                        disabled={loading || spinning}
                      >
                        {number}
                        {getBetAmount('number', number) > 0 && (
                          <div className="bet-chip">${getBetAmount('number', number)}</div>
                        )}
                      </button>
                    ))}
                  </div>
                  {/* Row 2: 2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35 */}
                  <div className="number-row">
                    {[2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35].map((number) => (
                      <button
                        key={number}
                        className={`number ${getNumberColor(number)}`}
                        onClick={() => placeBet('number', number)}
                        disabled={loading || spinning}
                      >
                        {number}
                        {getBetAmount('number', number) > 0 && (
                          <div className="bet-chip">${getBetAmount('number', number)}</div>
                        )}
                      </button>
                    ))}
                  </div>
                  {/* Row 3: 1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34 */}
                  <div className="number-row">
                    {[1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34].map((number) => (
                      <button
                        key={number}
                        className={`number ${getNumberColor(number)}`}
                        onClick={() => placeBet('number', number)}
                        disabled={loading || spinning}
                      >
                        {number}
                        {getBetAmount('number', number) > 0 && (
                          <div className="bet-chip">${getBetAmount('number', number)}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Outside Bets */}
              <div className="outside-bets">
                <button
                  className="outside-bet red"
                  onClick={() => placeBet('color', 'red')}
                  disabled={loading || spinning}
                >
                  Red
                  {getBetAmount('color', 'red') > 0 && (
                    <div className="bet-chip">${getBetAmount('color', 'red')}</div>
                  )}
                </button>
                <button
                  className="outside-bet black"
                  onClick={() => placeBet('color', 'black')}
                  disabled={loading || spinning}
                >
                  Black
                  {getBetAmount('color', 'black') > 0 && (
                    <div className="bet-chip">${getBetAmount('color', 'black')}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('odd', 'odd')}
                  disabled={loading || spinning}
                >
                  Odd
                  {getBetAmount('odd', 'odd') > 0 && (
                    <div className="bet-chip">${getBetAmount('odd', 'odd')}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('even', 'even')}
                  disabled={loading || spinning}
                >
                  Even
                  {getBetAmount('even', 'even') > 0 && (
                    <div className="bet-chip">${getBetAmount('even', 'even')}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('low', 'low')}
                  disabled={loading || spinning}
                >
                  1-18
                  {getBetAmount('low', 'low') > 0 && (
                    <div className="bet-chip">${getBetAmount('low', 'low')}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('high', 'high')}
                  disabled={loading || spinning}
                >
                  19-36
                  {getBetAmount('high', 'high') > 0 && (
                    <div className="bet-chip">${getBetAmount('high', 'high')}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('dozen', 1)}
                  disabled={loading || spinning}
                >
                  1st 12
                  {getBetAmount('dozen', 1) > 0 && (
                    <div className="bet-chip">${getBetAmount('dozen', 1)}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('dozen', 2)}
                  disabled={loading || spinning}
                >
                  2nd 12
                  {getBetAmount('dozen', 2) > 0 && (
                    <div className="bet-chip">${getBetAmount('dozen', 2)}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('dozen', 3)}
                  disabled={loading || spinning}
                >
                  3rd 12
                  {getBetAmount('dozen', 3) > 0 && (
                    <div className="bet-chip">${getBetAmount('dozen', 3)}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('column', 1)}
                  disabled={loading || spinning}
                >
                  1st Column
                  {getBetAmount('column', 1) > 0 && (
                    <div className="bet-chip">${getBetAmount('column', 1)}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('column', 2)}
                  disabled={loading || spinning}
                >
                  2nd Column
                  {getBetAmount('column', 2) > 0 && (
                    <div className="bet-chip">${getBetAmount('column', 2)}</div>
                  )}
                </button>
                <button
                  className="outside-bet"
                  onClick={() => placeBet('column', 3)}
                  disabled={loading || spinning}
                >
                  3rd Column
                  {getBetAmount('column', 3) > 0 && (
                    <div className="bet-chip">${getBetAmount('column', 3)}</div>
                  )}
                </button>
              </div>
            </div>

            {/* Game Actions */}
            <div className="game-actions">
              <div className="bet-info">
                <p>Total Bet: ${currentGame.totalBet.toLocaleString()}</p>
                <p>Your Money: ${user.money.toLocaleString()}</p>
              </div>
              
              <div className="action-buttons">
                <button
                  className="action-button clear"
                  onClick={clearBets}
                  disabled={loading || spinning || currentBets.length === 0}
                >
                  Clear Bets
                </button>
                
                <button
                  className="action-button spin"
                  onClick={spinWheel}
                  disabled={loading || spinning || currentBets.length === 0}
                >
                  {spinning ? 'Spinning...' : 'Spin Wheel'}
                </button>
              </div>
            </div>

            {/* Results */}
            {currentGame.gameState === 'complete' && currentGame.results.length > 0 && (
              <div className="game-results">
                <h4>Results</h4>
                {currentGame.results.map((result, index) => (
                  <div key={index} className={`result ${result.won ? 'win' : 'lose'}`}>
                    <span>{result.bet.type}: {result.bet.value}</span>
                    <span>{result.won ? `+$${result.payout.toLocaleString()}` : 'Lost'}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Chip Selection - Moved below betting board */}
            <div className="chip-selection">
              <h4>Select Chip Value</h4>
              <div className="chips">
                {chipValues.map((value) => (
                  <button
                    key={value}
                    className={`chip ${selectedChip === value ? 'selected' : ''}`}
                    onClick={() => setSelectedChip(value)}
                    disabled={loading || spinning}
                  >
                    ${value}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Roulette;
